"use client"

/**
 * See also: https://ui.shadcn.com/docs/components/data-table
 */

import {
    Column,
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    PaginationState,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table"

import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow,} from "@/components/ui/table.tsx"
import {ChangeEvent, useEffect, useLayoutEffect, useState} from "react";
import {useCommonTranslation, useTranslation} from "@/i18n/hooks";
import type {Namespace} from "@/i18n/types";
import {ArrowDownAZ, ArrowUpAZ, ArrowUpDown} from "lucide-react";
import {Link} from "@/components/tailwind/link.tsx";
import {Button} from "@/components/tailwind/button.tsx";
import {Text} from "@/components/tailwind/text.tsx";
import {Select} from "@/components/tailwind/select.tsx";
import {useUserSettingsStore} from "@/stores/user-settings.ts";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    'data-testid'?: string
    sorting?: SortingState
    onSortingChange?: (updaterOrValue: Updater<SortingState>) => void
    initialSorting?: SortingState
    /** Pass the client-side filter/search state; a changed value resets to the first page. */
    resetPageKey?: string | number | boolean | null
    getRowTestId?: (row: TData) => string
    getRowTestData?: (row: TData) => Record<string, string>
}

export function DataTable<TData, TValue>({
                                             columns,
                                             data,
                                             'data-testid': dataTestId,
                                             sorting: controlledSorting,
                                             onSortingChange: controlledOnSortingChange,
                                             initialSorting = [],
                                             resetPageKey,
                                             getRowTestId,
                                             getRowTestData,
                                         }: DataTableProps<TData, TValue>) {
    const {tableSettings, setPageSize} = useUserSettingsStore()
    const {t} = useCommonTranslation()
    const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>(initialSorting)
    const [pagination, setPagination] = useState<PaginationState>({
        pageSize: tableSettings.pageSize,
        pageIndex: 0
    })
    
    const sorting = controlledSorting ?? uncontrolledSorting
    const setSorting = controlledOnSortingChange ?? setUncontrolledSorting

    // Compare sorting by content, not identity, so controlled callers may pass inline literals
    const sortingKey = JSON.stringify(sorting)

    useEffect(() => {
        // Reset to first page when sorting or the caller's filter key changes
        setPagination(prev => prev.pageIndex === 0 ? prev : {...prev, pageIndex: 0})
    }, [sortingKey, resetPageKey])

    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            pageSize: tableSettings.pageSize
        }))
    }, [tableSettings])

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        // Keep the page stable across data mutations; the effect above handles shrinking data
        autoResetPageIndex: false,
        state: {
            sorting,
            pagination
        }
    })

    const lastPage = Math.max(0, table.getPageCount() - 1)

    useLayoutEffect(() => {
        // Clamp before paint so a shrinking data set never shows an empty page
        setPagination(prev => prev.pageIndex > lastPage ? {...prev, pageIndex: lastPage} : prev)
    }, [lastPage])

    const handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const newSize = Number(e.target.value)
        setPageSize(newSize)
        table.setPageSize(newSize)
    }

    return (
        <div data-testid={dataTestId}>
            <Table className="min-w-full text-left text-sm/6 text-zinc-950 dark:text-white">
                <TableHeader className="text-zinc-500 dark:text-zinc-400 border-b">
                    {table.getHeaderGroups().map((headerGroup) => (
                        <TableRow key={headerGroup.id}
                                  className="has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%] hover:bg-zinc-950/5 dark:hover:bg-white/5">
                            {headerGroup.headers.map((header) => {
                                return (
                                    <TableHead
                                        key={header.id}
                                        className="border-b-zinc-950/10 px-4 py-2 font-medium first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10 "
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </TableHead>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                                data-testid={getRowTestId ? getRowTestId(row.original) : undefined}
                                {...(getRowTestData ? Object.fromEntries(
                                    Object.entries(getRowTestData(row.original)).map(([key, value]) => 
                                        [`data-testdata-${key}`, value]
                                    )
                                ) : {})}
                                className="even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%] hover:bg-zinc-950/5 dark:hover:bg-white/5 has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%]"
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell key={cell.id}
                                               className="text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length}
                                       className="even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%] hover:bg-zinc-950/5 dark:hover:bg-white/5 has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%]">
                                {t("table.no-data")}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            <div className="flex flex-col gap-4 px-2 py-4 md:flex-row md:items-center md:justify-between">
                {/* Einträge pro Seite */}
                <div className="flex items-center justify-center gap-4">
                    <Text className="whitespace-nowrap">
                        {t("table.entriesPerPage")}
                    </Text>
                    <Select
                        value={pagination.pageSize.toString()}
                        onChange={handlePageSizeChange}
                        className="w-32"
                    >
                        {[10, 20, 30, 40, 50].map((size) => (
                            <option key={size} value={size.toString()}>
                                {size}
                            </option>
                        ))}
                    </Select>
                </div>

                <div className="text-center md:flex-1">
                    <Text>
                        {t("table.pageInfo", {
                            from: Math.min(pagination.pageIndex * pagination.pageSize + 1, table.getFilteredRowModel().rows.length),
                            to: Math.min((pagination.pageIndex + 1) * pagination.pageSize, table.getFilteredRowModel().rows.length),
                            total: table.getFilteredRowModel().rows.length
                        })}
                    </Text>
                </div>

                <div className="flex justify-center gap-2">
                    <Button
                        plain
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        title={t("table.firstPage")}
                    >
                        «
                    </Button>
                    <Button
                        plain
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        title={t("table.previousPage")}
                    >
                        ‹
                    </Button>
                    
                    {/* Page numbers */}
                    {(() => {
                        const totalPages = table.getPageCount()
                        const currentPage = pagination.pageIndex + 1
                        const pages: (number | string)[] = []
                        
                        if (totalPages <= 7) {
                            // Show all pages if 7 or fewer
                            for (let i = 1; i <= totalPages; i++) {
                                pages.push(i)
                            }
                        } else {
                            // Always show first page
                            pages.push(1)
                            
                            if (currentPage <= 3) {
                                // Near start: show 1 2 3 4 ... last
                                pages.push(2, 3, 4, '...', totalPages)
                            } else if (currentPage >= totalPages - 2) {
                                // Near end: show 1 ... last-3 last-2 last-1 last
                                pages.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
                            } else {
                                // Middle: show 1 ... current-1 current current+1 ... last
                                pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages)
                            }
                        }
                        
                        return pages.map((page, idx) => {
                            if (page === '...') {
                                return (
                                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-zinc-500">
                                        ...
                                    </span>
                                )
                            }
                            
                            const pageNum = page as number
                            const isActive = pageNum === currentPage
                            
                            return isActive ? (
                                <Button
                                    key={pageNum}
                                    onClick={() => table.setPageIndex(pageNum - 1)}
                                >
                                    {pageNum}
                                </Button>
                            ) : (
                                <Button
                                    key={pageNum}
                                    plain
                                    onClick={() => table.setPageIndex(pageNum - 1)}
                                >
                                    {pageNum}
                                </Button>
                            )
                        })
                    })()}
                    
                    <Button
                        plain
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        title={t("table.nextPage")}
                    >
                        ›
                    </Button>
                    <Button
                        plain
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                        title={t("table.lastPage")}
                    >
                        »
                    </Button>
                </div>
            </div>
        </div>
    )
}

interface SortableColumnHeaderProps<TData, TValue> {
    column: Column<TData, TValue>;
    i18nKey: string;
    namespace?: Namespace;
}

function SortableColumnHeader<TData, TValue>({column, i18nKey, namespace}: SortableColumnHeaderProps<TData, TValue>) {
    const { t } = useTranslation(namespace || 'common')
    
    return (
        <Link
            href="#"
            role="button"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
            {t(i18nKey as Parameters<typeof t>[0])}
            {column.getIsSorted() === false && (
                <ArrowUpDown size={12} className="inline"/>
            )}
            {column.getIsSorted() === "asc" && (
                <ArrowDownAZ size={12} className="inline"/>
            )}
            {column.getIsSorted() === "desc" && (
                <ArrowUpAZ size={12} className="inline"/>
            )}

        </Link>
    )
}

export function sortableColumnHeader<TData, TValue>(column: Column<TData, TValue>, i18nKey: string, namespace?: Namespace) {
    return <SortableColumnHeader column={column} i18nKey={i18nKey} namespace={namespace} />
}
